// src/pages/LMS/Courses/CoursePreview.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Card, CardContent,
  Accordion, AccordionSummary, AccordionDetails, Chip,
  Stack, IconButton, Divider, Grid, Avatar, Alert
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ExpandMore as ExpandIcon,
  PlayArrow as PlayIcon,
  Description as DocIcon,
  VideoLibrary as VideoIcon,
  Image as ImageIcon,
  Assignment as TestIcon,
  CheckCircle as CheckIcon,
  School as SchoolIcon,
  Timer as TimerIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const CoursePreview = () => {
  const { chatbotId, courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCoursePreview();
  }, [courseId]);

  const loadCoursePreview = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/lms/courses/${courseId}`, {
        params: { include_modules: true, include_content: true }
      });
      
      setCourse(response.data.data);
      setModules(response.data.data.modules || []);
    } catch (error) {
      console.error('Error loading course preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const getContentIcon = (contentType) => {
    const icons = {
      slide: <ImageIcon />,
      video: <VideoIcon />,
      document: <DocIcon />
    };
    return icons[contentType] || <DocIcon />;
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      beginner: 'success',
      intermediate: 'info',
      advanced: 'error'
    };
    return colors[difficulty] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, mt: 10, textAlign: 'center' }}>
        <Typography>Loading preview...</Typography>
      </Box>
    );
  }

  if (!course) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="error">Course not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => navigate(`/chatbot/${chatbotId}/lms/courses/${courseId}/builder`)}
            sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.100' } }}
          >
            <BackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700} sx={{ ml: 2, color: 'text.primary' }}>
            Course Preview
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => navigate(`/chatbot/${chatbotId}/lms/courses/${courseId}/builder`)}
            sx={{ borderColor: 'grey.300', color: 'text.primary' }}
          >
            Edit Course
          </Button>
          {course.status !== 'published' && (
            <Button 
              variant="contained" 
              sx={{ 
                bgcolor: '#10b981', 
                '&:hover': { bgcolor: '#059669' },
                boxShadow: 2
              }}
            >
              Publish Course
            </Button>
          )}
        </Stack>
      </Box>

      {/* Course Header Card - New Clean Design */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 4, 
          mb: 4, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3,
          boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)'
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
              <Chip
                label={course.category}
                size="small"
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.25)', 
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              />
              <Chip
                label={course.difficulty?.toUpperCase()}
                size="small"
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.25)', 
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              />
              <Chip
                label={course.status?.toUpperCase()}
                size="small"
                sx={{ 
                  bgcolor: course.status === 'published' ? 'rgba(16, 185, 129, 0.8)' : 'rgba(251, 191, 36, 0.8)',
                  color: 'white',
                  fontWeight: 600,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)'
                }}
              />
            </Stack>
            
            <Typography variant="h3" fontWeight={800} gutterBottom sx={{ letterSpacing: '-0.02em' }}>
              {course.title}
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 4, opacity: 0.95, lineHeight: 1.7 }}>
              {course.description}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={6} sm={3}>
                <Box>
                  <SchoolIcon sx={{ mb: 1, fontSize: 28, opacity: 0.9 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {modules.length}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Modules
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={6} sm={3}>
                <Box>
                  <TimerIcon sx={{ mb: 1, fontSize: 28, opacity: 0.9 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {course.duration_hours}h
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Duration
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={6} sm={3}>
                <Box>
                  <TrophyIcon sx={{ mb: 1, fontSize: 28, opacity: 0.9 }} />
                  <Typography variant="h5" fontWeight={700}>
                    {course.passing_score}%
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Passing Score
                  </Typography>
                </Box>
              </Grid>

              {course.certificate_enabled && (
                <Grid item xs={6} sm={3}>
                  <Box>
                    <CheckIcon sx={{ mb: 1, fontSize: 28, opacity: 0.9 }} />
                    <Typography variant="h5" fontWeight={700}>
                      Yes
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Certificate
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
            {course.thumbnail_url ? (
              <Box
                component="img"
                src={course.thumbnail_url}
                alt={course.title}
                sx={{
                  width: '100%',
                  maxWidth: 280,
                  borderRadius: 3,
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  border: '4px solid rgba(255, 255, 255, 0.2)'
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 200,
                  height: 200,
                  mx: 'auto',
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <SchoolIcon sx={{ fontSize: 100, opacity: 0.8 }} />
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Course Content */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: 'text.primary' }}>
          Course Curriculum
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {modules.length} modules • {course.total_content_items || 0} lessons
        </Typography>
      </Box>

      {modules.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px solid #e5e7eb' }}>
          <Typography variant="body1" color="text.secondary">
            No modules added yet
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {modules.map((module, moduleIndex) => (
            <Accordion 
              key={module.id} 
              defaultExpanded={moduleIndex === 0}
              elevation={0}
              sx={{ 
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                '&:before': { display: 'none' },
                overflow: 'hidden'
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandIcon />}
                sx={{ 
                  bgcolor: 'white',
                  '&:hover': { bgcolor: '#f9fafb' },
                  borderBottom: '1px solid #e5e7eb'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Avatar 
                    sx={{ 
                      mr: 2, 
                      bgcolor: '#667eea',
                      fontWeight: 700,
                      width: 40,
                      height: 40
                    }}
                  >
                    {moduleIndex + 1}
                  </Avatar>
                  <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, color: 'text.primary' }}>
                    {module.title}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
                    {module.submodules?.length > 0 && (
                      <Chip
                        label={`${module.submodules.length} lessons`}
                        size="small"
                        sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600 }}
                      />
                    )}
                    {module.has_knowledge_test && (
                      <Chip
                        icon={<TestIcon />}
                        label="Test"
                        size="small"
                        sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: '#fafafa', p: 3 }}>
                {module.description && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {module.description}
                  </Typography>
                )}

                {/* Submodules */}
                {module.submodules && module.submodules.length > 0 ? (
                  <Stack spacing={2}>
                    {module.submodules.map((submodule, subIndex) => (
                      <Card 
                        key={submodule.id} 
                        elevation={0}
                        sx={{ 
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          bgcolor: 'white'
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                            <Avatar 
                              sx={{ 
                                mr: 2, 
                                bgcolor: '#f3f4f6',
                                color: '#667eea',
                                fontWeight: 700,
                                width: 36,
                                height: 36,
                                fontSize: '0.875rem'
                              }}
                            >
                              {moduleIndex + 1}.{subIndex + 1}
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'text.primary' }}>
                                {submodule.title}
                              </Typography>
                              {submodule.description && (
                                <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 1 }}>
                                  {submodule.description}
                                </Typography>
                              )}
                              
                              {/* Content Items */}
                              {submodule.content_items && submodule.content_items.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom fontWeight={600}>
                                    CONTENT ({submodule.content_items.length})
                                  </Typography>
                                  <Stack spacing={1}>
                                    {submodule.content_items.map((content) => (
                                      <Box
                                        key={content.id}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          p: 1.5,
                                          bgcolor: '#f9fafb',
                                          borderRadius: 1.5,
                                          border: '1px solid #e5e7eb',
                                          transition: 'all 0.2s',
                                          '&:hover': {
                                            bgcolor: '#f3f4f6',
                                            borderColor: '#d1d5db'
                                          }
                                        }}
                                      >
                                        <Box sx={{ mr: 1.5, color: '#667eea' }}>
                                          {getContentIcon(content.content_type)}
                                        </Box>
                                        <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500 }}>
                                          {content.title}
                                        </Typography>
                                        {content.is_required && (
                                          <Chip
                                            label="Required"
                                            size="small"
                                            sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600 }}
                                          />
                                        )}
                                      </Box>
                                    ))}
                                  </Stack>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No lessons in this module yet
                  </Typography>
                )}

                {/* Knowledge Test Info */}
                {module.has_knowledge_test && (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mt: 2,
                      bgcolor: '#dbeafe',
                      color: '#1e40af',
                      border: '1px solid #93c5fd',
                      '& .MuiAlert-icon': { color: '#3b82f6' }
                    }} 
                    icon={<TestIcon />}
                  >
                    <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                      Knowledge Test Required
                    </Typography>
                    <Typography variant="body2">
                      Students must pass the test at the end of this module to proceed.
                      Passing score: {course.passing_score}%
                    </Typography>
                  </Alert>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}

      {/* Footer Actions */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 4, 
          mt: 4, 
          textAlign: 'center',
          borderRadius: 2,
          border: '1px solid #e5e7eb',
          bgcolor: 'white'
        }}
      >
        {(course.status != 'published') && (
          <>
            <Typography variant="h6" gutterBottom fontWeight={700} sx={{ color: 'text.primary' }}>
              Ready to publish this course?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Once published, students will be able to enroll and start learning
            </Typography>
          </>
        )}
        
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate(`/chatbot/${chatbotId}/lms/courses/${courseId}/builder`)}
            sx={{ 
              borderColor: '#d1d5db',
              color: 'text.primary',
              '&:hover': { 
                borderColor: '#9ca3af',
                bgcolor: '#f9fafb'
              }
            }}
          >
            Continue Editing
          </Button>
          {course.status !== 'published' && (
            <Button
              variant="contained"
              size="large"
              onClick={async () => {
                try {
                  await axiosInstance.post(`/api/lms/courses/${courseId}/publish`);
                  alert('Course published successfully!');
                  navigate(`/chatbot/${chatbotId}/lms`);
                } catch (error) {
                  console.error('Error publishing course:', error);
                  alert('Error publishing course');
                }
              }}
              sx={{ 
                bgcolor: '#10b981',
                '&:hover': { bgcolor: '#059669' },
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                px: 4
              }}
            >
              Publish Course
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default CoursePreview;